import View                     from '@widesk-ui/components/View';
import { observer }             from 'mobx-react-lite';
import Field                    from '@/TokamapEditor/ux/AreaSidebar/Field';
import InputOk                  from '@/TokamapEditor/ux/AreaSidebar/InputOk';
import ImportedImageBox         from '@/TokamapEditor/ux/ImportedImageBox';
import { isStorageUrl }         from '@/helpers/storageImageCrossOrigin';
import ImageThumbnail           from '@/TokamapEditor/ux/AreaSidebar/ImageThumbnail';
import validateImageUrl         from '@/TokamapEditor/helpers/validateImageUrl';
import replaceFeatureImage      from '@/TokamapEditor/helpers/replaceFeatureImage';
import currentEditorStore       from '@/TokamapEditor/stores/currentEditorStore';
import { commonValue }          from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import SliderInputNumberFeature from './SliderInputNumberFeature';
import SliderInputNumber        from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumber';
import { MIN_IMAGE_SCREEN_PX }  from '@/TokamapEditor/ux/constants';
import useDesignTokens          from '@/hooks/useDesignTokens';
import React                    from 'react';
import throttle                 from 'lodash/throttle';
import type { Shape }           from '@tokamap/core';
import type { DrawTriggerOptions } from '@tokamap/core';
import PlaceModel               from '@models/map/PlaceModel';
import shapePlaceId             from '@/TokamapEditor/helpers/shapePlaceId';
import { displayedShape }       from '@/TokamapEditor/helpers/placeLinkedContent';
import relinkPlaceImage         from '@/TokamapEditor/helpers/relinkPlaceImage';
import { PLACE_IMAGE_SOURCE }   from '@/TokamapEditor/helpers/placeLinkedContent';
import Segmented                from '@widesk-ui/Segmented';
import SettingsButton           from '@/TokamapEditor/ux/AreaSidebar/SettingsPopover';
import SectionTitle             from '@/TokamapEditor/ux/AreaSidebar/SectionTitle';
import { positionLabel }        from '@/TokamapEditor/ux/AreaSidebar/contentLabels';
import { ORIENTATION_LABELS }   from '@/TokamapEditor/ux/AreaSidebar/orientation';
import { AimOutlined }          from '@ant-design/icons';
import OrientationField         from '@/TokamapEditor/ux/AreaSidebar/OrientationField';
import { orientationModeOf }    from '@/TokamapEditor/ux/AreaSidebar/orientation';
import PositionGrid             from '@/TokamapEditor/ux/AreaSidebar/PositionGrid';
import HelpTip                  from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import { showsBadge }           from '@/TokamapEditor/helpers/shapeBadge';
import { themeImageLock }       from '@/TokamapEditor/helpers/themeImposed';
import { ThemeLockTag }         from '@/TokamapEditor/ux/AreaSidebar/ThemeLocked';
import { ThemeLockedControl }   from '@/TokamapEditor/ux/AreaSidebar/ThemeLocked';
import type { ShapeImageStyle } from '@tokamap/core';
import type { ThemeLock }       from '@/TokamapEditor/helpers/themeImposed';

const LIVE: DrawTriggerOptions = { skipHistory: true, skipSave: true, skipConfirm: true };

const SLIDER_MAX_MIN_SCREEN_PX = 200;
const LIVE_PREVIEW_INTERVAL_MS = 60;
const MAX_MIN_SCREEN_PX = 2000;

const AreaImageSection = observer(() => {
	const shapes = currentEditorStore.sidebarShapes;
	const selectedShape = currentEditorStore.selectedShape;
	const editor = currentEditorStore.get;
	const tokens = useDesignTokens();
	const isMultiSelection = currentEditorStore.isMultiSelection;

	if (!shapes.length) return null;

	// Une forme-image (`shapeKind: "image"`) porte déjà sa propre représentation visuelle en plein
	// cadre (voir `MapLibreAdapter.resolveShapeVisualProps`/`VertexResizingState.lockRatio`) : les
	// réglages de positionnement dans la boîte "contain" (taille/rotation/position/orientation) n'ont
	// pas de sens pour elle, seule l'URL et la transparence restent pertinentes.
	const isImage = shapes.every(s => s.shapeKind === 'image');
	const badged = showsBadge(shapes);

	const lock = themeImageLock(isImage ? [] : shapes);
	const lockedBy = (key: keyof ShapeImageStyle) => lock(key);
	const srcLock = lock('src');

	const single = shapes.length === 1 ? shapes.at(0) : undefined;
	const linkedShape = !isMultiSelection && single && shapePlaceId(single) && !srcLock ? single : undefined;
	const isLinked = linkedShape?.image?.source === PLACE_IMAGE_SOURCE;

	const imageSrc = (srcLock
		? srcLock.style.src
		: isLinked && linkedShape
			? displayedShape(linkedShape).image?.src
			: commonValue(shapes, s => s.image?.src)) || '';

	const isMixed = (key: keyof NonNullable<Shape['image']>) => {
		if (shapes.length < 2 || lockedBy(key as keyof ShapeImageStyle)) return false;
		return new Set(shapes.map(s => s.image?.[key])).size > 1;
	};

	const value = <K extends keyof ShapeImageStyle>(key: K) => {
		const locked = lock(key);

		return locked ? locked.style[key] : commonValue(shapes, s => s.image?.[key]);
	};

	const orientationLock = lock('rotation');
	const rotateWithMap = orientationLock ? !!orientationLock.style.rotateWithMap : commonValue(shapes, s => !!s.image?.rotateWithMap);
	// Angle par défaut : le cap de la vue de départ sur un cadre aligné au nord (un cercle), 0 sinon
	// (voir `ShapeImage.rotation`).
	const startRotation = editor.mapAdapter.getStartCamera()?.rotationDeg ?? 0;
	const reference = shapes[0];
	const northAligned = reference?.shapeKind === 'circle';
	// Le cap de la vue de départ est l'opposé de sa `rotationDeg` (voir `showStartView`).
	const defaultImageRotation = northAligned ? Math.round((((-startRotation) % 360) + 360) % 360) % 360 : 0;
	const imported = !isMultiSelection && !!selectedShape && !srcLock && isStorageUrl(imageSrc);
	const positionLock = lockedBy('horizontalAlign') ?? lockedBy('verticalAlign');
	const placeName = PlaceModel.localize('default', 'l');

	const percent = (ratio: number) => `${Math.round(ratio * 100)} %`;
	const scale = value('scale') ?? 1;
	const radius = value('radius');
	const opacity = value('opacity') ?? 1;
	const minScreenPx = value('minScreenPx') ?? MIN_IMAGE_SCREEN_PX;
	const frameMixed = isMixed('scale') || isMixed('radius');
	const frameSummary = [!isImage && percent(scale), `coins ${percent((radius ?? 0) * 2)}`].filter(Boolean).join(' · ');
	const frameDetails = [
		opacity !== 1 && `opacité ${percent(opacity)}`,
		minScreenPx !== MIN_IMAGE_SCREEN_PX && (minScreenPx ? `masquée sous ${minScreenPx} px` : 'toujours affichée'),
	].filter((detail): detail is string => !!detail);

	const orientationMode = orientationModeOf({ rotation: value('rotation'), rotateWithMap: !!rotateWithMap });
	const positionMixed = isMixed('horizontalAlign') || isMixed('verticalAlign') || rotateWithMap === undefined || isMixed('rotation');
	const positionSummary = [
		positionLabel(value('horizontalAlign') ?? 0.5, value('verticalAlign') ?? 0.5),
		orientationMode === 'fixed' ? `angle ${value('rotation') ?? defaultImageRotation}°` : ORIENTATION_LABELS[orientationMode],
	].join(' · ');

	return (
		<View gap="sm">
			{!isImage && <SectionTitle>Image</SectionTitle>}

			<View gap="sm">
				{!!srcLock && (
					<View row spread centerV>
						<View size={13} style={{ fontWeight: 600 }}>Image</View>
						<ThemeLockTag lock={srcLock} />
					</View>
				)}

				<ThemeLockedControl lock={srcLock}>
					{!!linkedShape && (
						<Segmented
							block
							value={isLinked ? 'place' : 'free'}
							onChange={choice => {
								if (choice === 'place') relinkPlaceImage(linkedShape);
								else editor.setShapeImage(linkedShape.id, { src: imageSrc, source: undefined });
							}}
							options={[
								{ value: 'place', label: `Image du ${placeName}` },
								{ value: 'free', label: 'Image libre' },
							]}
						/>
					)}

					{linkedShape && isLinked ? (
						!imageSrc && (
							<View size={12.5} color={tokens.text.muted}>
								Pas encore d'image : ajoutez-la sur la vignette en haut.
							</View>
						)
					) : (
						<View gap={6}>
							<View row gap="xs">
								{!isMultiSelection && !!selectedShape && (
									<ImageThumbnail
										src={imageSrc}
										onPick={() => replaceFeatureImage(selectedShape, imageSrc ? undefined : 'Ajouter une image')}
									/>
								)}

								<View flex>
									{imported ? (
										<ImportedImageBox
											onReplace={() => replaceFeatureImage(selectedShape)}
											onRemove={() => editor.setShapeImage(selectedShape.id, { src: undefined })}
										/>
									) : (
										<InputOk
											value={imageSrc}
											mixed={isMixed('src')}
											debounce={600}
											onSubmit={async input => {
												const trimmed = (input || '').trim();

												validateImageUrl(trimmed);

												await editor.setShapeImage(shapes.map(s => s.id), { src: trimmed });
											}}
										/>
									)}
								</View>
							</View>

							{!imported && (
								<View size={12} color={tokens.text.muted}>
									Collez une adresse ou cliquez sur la miniature.
								</View>
							)}
						</View>
					)}
				</ThemeLockedControl>
			</View>

			{!!imageSrc && !badged && (
				<View style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
					<div style={{ gridColumn: isImage ? '1 / -1' : undefined, minWidth: 0 }}>
						<SettingsButton
							width={300}
							label="Taille et cadrage"
							summary={frameMixed ? 'Plusieurs valeurs' : frameSummary}
							details={frameDetails}
							mixed={frameMixed}
							themed={!!(lockedBy('scale') ?? lockedBy('radius') ?? lockedBy('opacity') ?? lock('minScreenPx'))}
							preview={(
								<span
									style={{
										width: 24,
										height: 24,
										flexShrink: 0,
										boxSizing: 'border-box',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										borderRadius: 3,
										border: `1px dashed ${tokens.border.default}`,
									}}
								>
									<span
										style={{
											width: `${Math.max(isImage ? 1 : scale, 0.3) * 100}%`,
											height: `${Math.max(isImage ? 1 : scale, 0.3) * 100}%`,
											borderRadius: `${(radius ?? 0) * 100}%`,
											background: `color-mix(in srgb, ${tokens.text.secondary} 45%, transparent)`,
										}}
									/>
								</span>
							)}
						>
							{!isImage && (
								<Field lockedBy={lockedBy('scale')} label={<>Taille<HelpTip>Part de la forme occupée par l'image.</HelpTip></>}>
									<SliderInputNumberFeatureIcon
										property="scale"
										initialValue={value('scale') ?? 1}
										toModelValue={v => v / 100}
										fromModelValue={v => v * 100}
										isPercent
										mixed={isMixed('scale')}
									/>
								</Field>
							)}

							<Field lockedBy={lockedBy('radius')} label={<>Arrondi des coins<HelpTip>À 100 %, l'image est découpée en cercle.</HelpTip></>}>
								<SliderInputNumberFeatureIcon
									property="radius"
									initialValue={value('radius') ?? 0}
									toModelValue={v => v / 200}
									fromModelValue={v => v * 200}
									isPercent
									mixed={isMixed('radius')}
								/>
							</Field>

							<Field label="Opacité" lockedBy={lockedBy('opacity')}>
								<SliderInputNumberFeatureIcon
									property="opacity"
									initialValue={value('opacity') ?? 1}
									toModelValue={v => v / 100}
									fromModelValue={v => v * 100}
									isPercent
									mixed={isMixed('opacity')}
								/>
							</Field>

							<SectionTitle>Réglage fin</SectionTitle>

							<MinScreenPxField lock={lock('minScreenPx')} />
						</SettingsButton>
					</div>

					{!isImage && (
						<SettingsButton
							width={300}
							label="Position"
							summary={positionMixed ? 'Plusieurs valeurs' : positionSummary}
							mixed={positionMixed}
							themed={!!(positionLock ?? orientationLock)}
							preview={<AimOutlined style={{ fontSize: 22, color: tokens.text.secondary, flexShrink: 0 }} />}
						>
							<View row gap="md">
								<ThemeLockedControl lock={positionLock}>
									<PositionGrid
										horizontal={isMixed('horizontalAlign') ? undefined : (value('horizontalAlign') ?? 0.5)}
										vertical={isMixed('verticalAlign') ? undefined : (value('verticalAlign') ?? 0.5)}
										onPick={(horizontalAlign, verticalAlign) => editor.setShapeImage(
											shapes.map(s => s.id),
											{ horizontalAlign, verticalAlign },
										)}
									/>
								</ThemeLockedControl>

								<View size={12} color={tokens.text.muted} lineHeight={1.45} style={{ paddingTop: 4 }}>
									Un clic place l'image dans la forme.
								</View>
							</View>

							<OrientationField
								lockedBy={orientationLock}
								mode={orientationMode}
								mixed={rotateWithMap === undefined || isMixed('rotation')}
								effectiveRotation={value('rotation') ?? defaultImageRotation}
								onChange={values => editor.setShapeImage(shapes.map(s => s.id), values)}
								slider={(
									<SliderInputNumberFeatureIcon
										property="rotation"
										initialValue={value('rotation') ?? defaultImageRotation}
										toModelValue={v => v}
										fromModelValue={v => v}
										inputProps={{ min: 0, max: 359, suffix: '°', step: 1 }}
									/>
								)}
							/>

							<SectionTitle>Réglage fin</SectionTitle>

							<Field label="Position horizontale" lockedBy={lockedBy('horizontalAlign')}>
								<SliderInputNumberFeatureIcon
									property="horizontalAlign"
									initialValue={value('horizontalAlign') ?? 0.5}
									toModelValue={v => v / 100}
									fromModelValue={v => v * 100}
									isPercent
									mixed={isMixed('horizontalAlign')}
								/>
							</Field>

							<Field label="Position verticale" lockedBy={lockedBy('verticalAlign')}>
								<SliderInputNumberFeatureIcon
									property="verticalAlign"
									initialValue={value('verticalAlign') ?? 0.5}
									toModelValue={v => v / 100}
									fromModelValue={v => v * 100}
									isPercent
									mixed={isMixed('verticalAlign')}
								/>
							</Field>
						</SettingsButton>
					)}
				</View>
			)}
		</View>
	);
});

const MinScreenPxField = observer(({ lock }: { lock?: ThemeLock<ShapeImageStyle>; }) => {
	const shapes = currentEditorStore.sidebarShapes;
	const tokens = useDesignTokens();

	if (!shapes.length) return null;

	const read = (shape: Shape) => (lock ? lock.style.minScreenPx : shape.image?.minScreenPx) ?? MIN_IMAGE_SCREEN_PX;
	const mixed = new Set(shapes.map(read)).size > 1;

	const save = (input: number | undefined, options?: DrawTriggerOptions) => {
		const px = Math.max(0, Number(input) || 0);

		return currentEditorStore.get.setShapeImage(
			shapes.map(s => s.id),
			{ minScreenPx: px === MIN_IMAGE_SCREEN_PX ? undefined : px },
			options,
		);
	};

	return (
		<Field
			lockedBy={lock}
			label={(
				<>
					Taille minimale d'affichage
					<HelpTip>
						En dessous de cette taille à l'écran, l'image est masquée. À 0, elle est toujours affichée,
						ce qui ralentit une carte vue de loin si beaucoup d'images le sont.
					</HelpTip>
				</>
			)}
		>
			<View gap={4}>
				<SliderInputNumber
					value={read(shapes[0]!)}
					min={0}
					max={SLIDER_MAX_MIN_SCREEN_PX}
					inputMax={MAX_MIN_SCREEN_PX}
					step={5}
					suffix="px"
					onChange={input => save(input, LIVE)}
					onAfterChange={input => save(input)}
				/>

				{mixed && (
					<View size={12} color={tokens.text.muted}>
						Valeurs différentes
					</View>
				)}
			</View>
		</Field>
	);
});

const IMAGE_DEFAULTS: Partial<Record<string, number>> = {
	scale: 1,
	opacity: 1,
	rotation: 0,
	radius: 0,
	horizontalAlign: 0.5,
	verticalAlign: 0.5,
};

const SliderInputNumberFeatureIcon = ({
	property,
	initialValue,
	toModelValue,
	fromModelValue,
	inputProps,
	isPercent,
	mixed,
}: {
	property: 'scale' | 'rotation' | 'opacity' | 'radius' | 'horizontalAlign' | 'verticalAlign';
	initialValue: number;
	toModelValue: (v: number) => number;
	fromModelValue: (v: number) => number;
	inputProps?: any;
	isPercent?: boolean;
	mixed?: boolean;
}) => {
	const startValue = React.useRef(initialValue);
	const [loading, setLoading] = React.useState(false);

	const applyToSelection = (value: number, options?: Parameters<typeof currentEditorStore.get.setShapeImage>[2]) => {
		return currentEditorStore.get.setShapeImage(
			currentEditorStore.sidebarShapes.map(s => s.id),
			{ [property]: value },
			options,
		);
	};

	// Aperçu en direct limité à une mise à jour toutes les 60 ms.
	const applyLive = React.useMemo(() => throttle(
		(value: number) => applyToSelection(value, { skipSave: true, skipHistory: true, skipConfirm: true }),
		LIVE_PREVIEW_INTERVAL_MS,
	), [property]);

	React.useEffect(() => () => applyLive.cancel(), [applyLive]);

	return (
		<SliderInputNumberFeature
			loading={loading}
			resetValue={IMAGE_DEFAULTS[property] === undefined ? undefined : fromModelValue(IMAGE_DEFAULTS[property]!)}
			value={fromModelValue(initialValue)}
			isPercent={isPercent}
			mixed={mixed}
			inputProps={inputProps}
			onStartChange={() => {
				startValue.current = initialValue;
			}}
			onChange={v => {
				applyLive(toModelValue(v));
			}}
			onAfterChange={async v => {
				applyLive.cancel();
				setLoading(true);
				await applyToSelection(toModelValue(v));
				setLoading(false);
			}}
		/>
	);
};

export default AreaImageSection;
